import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useIvrs, useSaveIvr, useDeleteTms } from '../../lib/api/hooks-tms.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { Switch } from '../../components/ui/Form.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import { api, ApiClientError } from '../../lib/api/client.js';
import type { IvrMenu, Destination } from '@arcnet/contracts';

interface KeyDraft { label: string; destination: Destination | null }
type KeyMap = Record<string, KeyDraft>;

/**
 * The twelve keys a caller actually has, in handset order.
 *
 * Modelling the options as a map keyed by digit rather than as a list of rows
 * makes a duplicate key unrepresentable: the old editor let two rows claim
 * "1" and only warned about it. Here a second assignment simply replaces the
 * first, which is what the switch would have done anyway.
 */
const KEYS: ReadonlyArray<{ digit: string; sub: string }> = [
  { digit: '1', sub: '' },     { digit: '2', sub: 'abc' },  { digit: '3', sub: 'def' },
  { digit: '4', sub: 'ghi' },  { digit: '5', sub: 'jkl' },  { digit: '6', sub: 'mno' },
  { digit: '7', sub: 'pqrs' }, { digit: '8', sub: 'tuv' },  { digit: '9', sub: 'wxyz' },
  { digit: '*', sub: '' },     { digit: '0', sub: 'oper' }, { digit: '#', sub: '' },
];

function Dialpad({ tenantId, keys, selected, onSelect }: {
  tenantId: string; keys: KeyMap; selected: string; onSelect: (d: string) => void;
}) {
  return (
    <div className="arc-dialpad" role="group" aria-label="IVR keys">
      {KEYS.map(({ digit, sub }) => {
        const opt = keys[digit];
        const assigned = Boolean(opt?.destination);
        return (
          <button
            key={digit} type="button" className="arc-key"
            data-assigned={assigned} data-selected={selected === digit}
            aria-pressed={selected === digit}
            aria-label={`Key ${digit}${assigned ? ', assigned' : ', not assigned'}`}
            title={opt?.label || undefined}
            onClick={() => onSelect(digit)}
          >
            {assigned && <span className="arc-key-dot" />}
            <span className="arc-key-digit">{digit}</span>
            {assigned ? (
              <span className="arc-key-label">
                {opt?.label
                  ? opt.label
                  : <DestinationLabel tenantId={tenantId} destination={opt?.destination ?? null} />}
              </span>
            ) : (
              <span className="arc-key-sub">{sub}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function IvrsTab({ tenantId }: { tenantId: string }) {
  const ivrs = useIvrs(tenantId);
  const del = useDeleteTms(tenantId, 'ivrs');
  const [editing, setEditing] = useState<IvrMenu | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">IVR menus</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            "Press 1 for sales" — a greeting and a set of keys.
          </p>
        </div>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'New IVR'}
        </button>
      </div>

      {ivrs.error && <ErrorBanner error={ivrs.error} />}
      {del.error && <ErrorBanner error={del.error} />}
      {(creating || editing) && (
        <IvrEditor tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }} />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Dial</th><th>Options</th>
                <th className="arc-td-num">Timeout</th><th>On no input</th><th />
              </tr>
            </thead>
            {ivrs.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {ivrs.data?.data.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontWeight: 550 }}>{m.name}</td>
                    <td className="arc-mono">{m.extension ?? <span className="arc-subtle">—</span>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {m.options.length === 0
                          ? <span className="arc-subtle">none</span>
                          : m.options.map((o) => (
                              <Badge key={o.id} tone="muted">
                                <span className="arc-mono" style={{ fontSize: 11 }}>{o.digit}</span>
                                {o.label ? ` ${o.label}` : ''}
                              </Badge>
                            ))}
                      </div>
                    </td>
                    <td className="arc-td-num">{m.timeout_sec}s</td>
                    <td><DestinationLabel tenantId={tenantId} destination={m.timeout_destination} /></td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { setEditing(m); setCreating(false); }}>Edit</button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { if (confirm(`Delete IVR '${m.name}'?`)) del.mutate(m.id); }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!ivrs.isLoading && ivrs.data?.data.length === 0 && (
          <Empty icon="ivr" title="No IVR menus"
            hint="An IVR answers, plays a greeting and routes on the key pressed." />
        )}
      </div>
    </>
  );
}

function IvrEditor({ tenantId, existing, onDone }: {
  tenantId: string; existing: IvrMenu | null; onDone: () => void;
}) {
  const save = useSaveIvr(tenantId);
  const [name, setName] = useState(existing?.name ?? '');
  const [ext, setExt] = useState(existing?.extension ?? '');
  const [timeoutSec, setTimeoutSec] = useState(String(existing?.timeout_sec ?? 5));
  const [directDial, setDirectDial] = useState(existing?.direct_dial ?? false);
  const [onTimeout, setOnTimeout] = useState<Destination | null>(existing?.timeout_destination ?? null);
  const [greetingId, setGreetingId] = useState<string>(existing?.greeting_media_id ?? '');

  /**
   * Greetings and announcements both, because the distinction is about where a
   * file is normally used rather than what a menu is allowed to play.
   */
  const media = useQuery({
    queryKey: ['media', tenantId],
    queryFn: () => api.get<{ data: Array<{ id: string; kind: string; name: string; duration_sec: number | null }> }>(
      `/v1/tenants/${tenantId}/media`),
    enabled: !!tenantId,
  });
  const greetings = (media.data?.data ?? [])
    .filter((m) => m.kind === 'greeting' || m.kind === 'announcement');
  const [keys, setKeys] = useState<KeyMap>(() => {
    const init: KeyMap = {};
    for (const o of existing?.options ?? []) {
      init[o.digit] = { label: o.label ?? '', destination: o.destination };
    }
    return init;
  });
  const [selected, setSelected] = useState('1');

  const setKey = (digit: string, patch: Partial<KeyDraft>) =>
    setKeys((prev) => ({
      ...prev,
      [digit]: { label: '', destination: null, ...prev[digit], ...patch },
    }));

  const current = keys[selected];
  const assignedKeys = KEYS.filter((k) => keys[k.digit]?.destination);
  // With direct dial on, a key bound to an option is consumed as that option,
  // so extensions starting with that digit become unreachable from the menu.
  const shadowed = directDial
    ? assignedKeys.map((k) => k.digit).filter((d) => d >= '0' && d <= '9')
    : [];
  const err = save.error instanceof ApiClientError ? save.error : null;

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 14 }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate({
          ...(existing ? { id: existing.id } : {}),
          name, extension: ext || null, timeout_sec: Number(timeoutSec),
          greeting_media_id: greetingId || null,
          direct_dial: directDial, timeout_destination: onTimeout,
          options: KEYS.flatMap(({ digit }) => {
            const k = keys[digit];
            if (!k?.destination) return [];
            return [{
              digit,
              ...(k.label ? { label: k.label } : {}),
              destination: k.destination,
            }];
          }),
        }, { onSuccess: onDone });
      }}
    >
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.name}` : 'New IVR menu'}
      </h3>
      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 110px 120px', gap: 12, marginBottom: 14 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iv-name">Name</label>
          <input id="iv-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Main menu" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iv-ext">Dial code</label>
          <input id="iv-ext" className="arc-input" value={ext}
            onChange={(e) => setExt(e.target.value)} placeholder="400" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="iv-to">Wait for key (s)</label>
          <input id="iv-to" className="arc-input" inputMode="numeric" value={timeoutSec}
            onChange={(e) => setTimeoutSec(e.target.value)} />
        </div>
      </div>

      <div className="arc-field">
        <label className="arc-label" htmlFor="iv-greeting">Greeting</label>
        <select id="iv-greeting" className="arc-input" style={{ maxWidth: 420 }}
          value={greetingId} onChange={(e) => setGreetingId(e.target.value)}>
          <option value="">No greeting — wait in silence</option>
          {greetings.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}{m.duration_sec ? ` (${m.duration_sec.toFixed(1)}s)` : ''}
            </option>
          ))}
        </select>
        {/* Interruptible, and worth saying: it changes how you write the
            recording. "Press 1 for sales" belongs at the start, not after a
            welcome nobody who calls twice will listen to. */}
        <p className="arc-hint">
          {greetings.length === 0
            ? 'Nothing uploaded yet — add one under Media, then choose it here.'
            : 'Plays while the menu waits. A caller can press a key over it.'}
        </p>
      </div>

      <div className="arc-field">
        <div className="arc-row" style={{ marginBottom: 6 }}>
          <label className="arc-label" style={{ margin: 0 }}>Key options</label>
          <div className="arc-spacer" />
          <span className="arc-muted" style={{ fontSize: 11.5 }}>
            {assignedKeys.length} of {KEYS.length} keys assigned
          </span>
        </div>

        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Dialpad tenantId={tenantId} keys={keys} selected={selected} onSelect={setSelected} />

          <div className="arc-key-panel">
            <div className="arc-key-panel-head">
              <span className="arc-key-chip">{selected}</span>
              <div className="arc-stack">
                <strong style={{ fontSize: 12.5 }}>Key {selected}</strong>
                <span className="arc-muted" style={{ fontSize: 11.5 }}>
                  Where the caller goes when they press it.
                </span>
              </div>
              <div className="arc-spacer" />
              {current?.destination && (
                <button
                  type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                  onClick={() => setKeys((prev) => {
                    const next = { ...prev };
                    delete next[selected];
                    return next;
                  })}
                >
                  <Icon name="trash" size={12} /> Clear
                </button>
              )}
            </div>

            <div className="arc-field">
              <label className="arc-label">Send to</label>
              <DestinationPicker
                tenantId={tenantId} value={current?.destination ?? null}
                onChange={(d) => setKey(selected, { destination: d })} allowNone
              />
            </div>

            <div className="arc-field" style={{ marginBottom: 0 }}>
              <label className="arc-label" htmlFor="iv-klabel">Label</label>
              <input
                id="iv-klabel" className="arc-input" placeholder="Sales"
                value={current?.label ?? ''}
                onChange={(e) => setKey(selected, { label: e.target.value })}
              />
              <p className="arc-muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                Shown on the keypad and in reports. Callers never hear it.
              </p>
            </div>
          </div>
        </div>

        {shadowed.length > 0 && (
          <div className="arc-banner arc-banner-info" style={{ marginTop: 10 }}>
            <Icon name="info" size={14} />
            <span>
              Direct dial is on, so extensions starting with{' '}
              <span className="arc-mono">{shadowed.join(', ')}</span>{' '}
              cannot be reached from this menu — those keys are taken by an option.
            </span>
          </div>
        )}
      </div>

      <div className="arc-row" style={{ marginBottom: 12, gap: 10 }}>
        <Switch id="iv-dd" checked={directDial} onChange={setDirectDial} />
        <label htmlFor="iv-dd" style={{ fontSize: 12.5, cursor: 'pointer' }}>
          Allow callers to dial an extension directly
        </label>
      </div>

      <div className="arc-field">
        <label className="arc-label">If nothing is pressed</label>
        <DestinationPicker tenantId={tenantId} value={onTimeout} onChange={setOnTimeout} allowNone />
      </div>

      <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create IVR'}
      </button>
    </form>
  );
}
